import "./productSlider.scss";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react/swiper-react.js";
import { Navigation, Thumbs } from "swiper";
import "swiper/swiper.min.css";
import "swiper/modules/pagination/pagination.min.css";
import React, { useEffect, useState, useRef, useMemo } from "react";
import Placeholder from "../../../assets/images/placeholder.gif";
import { LazyLoadImage } from "react-lazy-load-image-component";
import Magnifier from "react-magnifier";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { FaYoutube, FaTiktok } from "react-icons/fa";

const ProductImagesSlider = (props) => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [activeThumb, setActiveThumb] = useState(null);
  const [mainSwiper, setMainSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const getYoutubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const getTikTokId = (url) => {
    if (!url) return null;
    const match = url.match(/\/video\/(\d+)/);
    return match ? match[1] : null;
  };

  const videoUrl =
    props.item?.api?.videos?.map((v) => {
      return { video_url: v.url, type: "video" };
    }) || [];

  const youtubeLink = props.item?.api?.youtube_link;
  let embedVideo = [];

  if (youtubeLink) {
    if (youtubeLink.includes("tiktok.com")) {
      const tiktokId = getTikTokId(youtubeLink);
      if (tiktokId) {
        embedVideo.push({
          video_url: `https://www.tiktok.com/embed/v2/${tiktokId}`,
          type: "tiktok",
        });
      }
    } else {
      embedVideo.push({
        video_url: `${youtubeLink}${youtubeLink.includes("?") ? "&" : "?"}autoplay=1&mute=1`,
        type: "youtube",
      });
    }
  }

  const rawImages = Array.isArray(props.images) ? props.images : [];
  const allUrl = [...videoUrl, ...rawImages, ...embedVideo];

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.innerWidth <= 767 ||
      (window.matchMedia && window.matchMedia("(max-width: 767px)").matches)
    );
  });

  // Extract all zoomable images from allUrl (excluding videos and embed players)
  const zoomImages = useMemo(() => {
    return allUrl
      .map((item, originalIndex) => {
        if (
          item?.type === "video" ||
          item?.type === "youtube" ||
          item?.type === "tiktok"
        ) {
          return null;
        }
        const src =
          item && typeof item === "string"
            ? item
            : item?.original_image_url
            ? item.original_image_url
            : item?.large_image_url
            ? item.large_image_url
            : item?.url
            ? item.url
            : Placeholder;
        return { src, originalIndex };
      })
      .filter(Boolean);
  }, [allUrl]);

  const [modalIndex, setModalIndex] = useState(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const openZoomModal = (target) => {
    if (!isMobile) return;
    let targetIdx = 0;
    if (typeof target === "number") {
      const found = zoomImages.findIndex((img) => img.originalIndex === target);
      targetIdx = found !== -1 ? found : target;
    } else if (typeof target === "string") {
      const found = zoomImages.findIndex((img) => img.src === target);
      targetIdx = found !== -1 ? found : 0;
    }
    if (zoomImages.length > 0) {
      setModalIndex(Math.min(Math.max(0, targetIdx), zoomImages.length - 1));
    }
  };

  const closeZoomModal = (e) => {
    if (e) {
      e.stopPropagation();
      if (typeof e.preventDefault === "function") e.preventDefault();
    }
    setModalIndex(null);
  };

  const nextZoomImage = (e) => {
    if (e) {
      e.stopPropagation();
      if (typeof e.preventDefault === "function") e.preventDefault();
    }
    if (zoomImages.length <= 1 || modalIndex === null) return;
    const nextIdx = (modalIndex + 1) % zoomImages.length;
    setModalIndex(nextIdx);
    const origIdx = zoomImages[nextIdx]?.originalIndex;
    if (origIdx !== undefined && mainSwiper && !mainSwiper.destroyed) {
      if (typeof mainSwiper.slideToLoop === "function") {
        mainSwiper.slideToLoop(origIdx);
      } else {
        mainSwiper.slideTo(origIdx);
      }
      setActiveIndex(origIdx);
    }
  };

  const prevZoomImage = (e) => {
    if (e) {
      e.stopPropagation();
      if (typeof e.preventDefault === "function") e.preventDefault();
    }
    if (zoomImages.length <= 1 || modalIndex === null) return;
    const prevIdx = (modalIndex - 1 + zoomImages.length) % zoomImages.length;
    setModalIndex(prevIdx);
    const origIdx = zoomImages[prevIdx]?.originalIndex;
    if (origIdx !== undefined && mainSwiper && !mainSwiper.destroyed) {
      if (typeof mainSwiper.slideToLoop === "function") {
        mainSwiper.slideToLoop(origIdx);
      } else {
        mainSwiper.slideTo(origIdx);
      }
      setActiveIndex(origIdx);
    }
  };

  const handleModalTouchStart = (e) => {
    if (e.touches && e.touches.length === 1) {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    }
  };

  const handleModalTouchEnd = (e) => {
    if (
      touchStartX.current !== null &&
      touchStartY.current !== null &&
      e.changedTouches &&
      e.changedTouches.length === 1
    ) {
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      const deltaY = e.changedTouches[0].clientY - touchStartY.current;
      touchStartX.current = null;
      touchStartY.current = null;

      if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
        if (deltaX < 0) {
          nextZoomImage();
        } else {
          prevZoomImage();
        }
      }
    }
  };

  // Lock body scroll and listen for Escape / Arrow keys when modal is open
  useEffect(() => {
    if (modalIndex !== null) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          closeZoomModal();
        } else if (e.key === "ArrowRight") {
          nextZoomImage();
        } else if (e.key === "ArrowLeft") {
          prevZoomImage();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = originalOverflow;
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [modalIndex, zoomImages.length]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      const narrow = window.innerWidth <= 767;
      setIsMobile(narrow);
    };
    window.addEventListener("resize", onResize);
    onResize();
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Reset active index when product changes
  useEffect(() => {
    setActiveIndex(0);
    if (mainSwiper && !mainSwiper.destroyed) {
      if (typeof mainSwiper.slideToLoop === "function") {
        mainSwiper.slideToLoop(0);
      } else {
        mainSwiper.slideTo(0);
      }
    }
  }, [props.item?.id, props.images]);

  const arrowStyle = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 10,
    background: "rgba(0, 0, 0, 0.45)",
    border: "1px solid rgba(255, 122, 0, 0.4)",
    borderRadius: "50%",
    width: "42px",
    height: "42px",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    color: "#F5B96D",
    transition: "all 0.25s ease",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
  };

  const onMouseEnter = (e) => {
    e.currentTarget.style.background = "rgba(245, 185, 109, 0.25)";
    e.currentTarget.style.borderColor = "#F5B96D";
    e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
    e.currentTarget.style.color = "#ffffff";
  };

  const onMouseLeave = (e) => {
    e.currentTarget.style.background = "rgba(0, 0, 0, 0.45)";
    e.currentTarget.style.borderColor = "rgba(245, 185, 109, 0.4)";
    e.currentTarget.style.transform = "translateY(-50%) scale(1)";
    e.currentTarget.style.color = "#F5B96D";
  };

  const handleThumbClick = (index) => {
    setActiveIndex(index);
    if (mainSwiper && !mainSwiper.destroyed) {
      if (typeof mainSwiper.slideToLoop === "function") {
        mainSwiper.slideToLoop(index);
      } else {
        mainSwiper.slideTo(index);
      }
    }
  };

  return (
    <div className="product-gallery-section" style={{ width: "100%", display: "block" }}>
      {/* ── Main Slider Wrapper (Contains ONLY the main slider and its navigation arrows) ── */}
      <div
        className="product-main-slider-wrap"
        style={{
          position: "relative",
          width: "100%",
        }}
      >
        <Swiper
          loop={allUrl.length > 1}
          spaceBetween={10}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          onInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            if (swiper.navigation) {
              swiper.navigation.init();
              swiper.navigation.update();
            }
            setMainSwiper(swiper);
          }}
          onSlideChange={(swiper) => {
            const idx = swiper.realIndex !== undefined ? swiper.realIndex : swiper.activeIndex;
            setActiveIndex(idx);
          }}
          onSwiper={setMainSwiper}
          modules={[Navigation, Thumbs]}
          grabCursor={true}
          thumbs={{
            swiper: activeThumb && !activeThumb.destroyed ? activeThumb : null,
          }}
          className={`product-images-slider myImgWidth ${
            isMobile ? "product-images-slider--mobile" : "my-2"
          }`}
          style={{
            border: "1px solid rgb(236 132 95 / 50%)",
            borderRadius: "6px",
            width: "100%",
            overflow: "hidden",
            ...(isMobile ? { height: "clamp(420px, 75vh, 720px)" } : {}),
          }}
          autoHeight={isMobile}
        >
          {allUrl.length !== 0 &&
            allUrl.map((item, index) => {
              const srcValue =
                item && typeof item === "string"
                  ? item
                  : item?.original_image_url
                    ? item.original_image_url
                    : Placeholder;

              return (
                <SwiperSlide key={index}>
                  {item?.type === "video" ? (
                    <div>
                      <video
                        controls
                        autoPlay
                        style={{ height: "609px", width: "391px" }}
                        loading="lazy"
                      >
                        <source src={item.video_url} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ) : item?.type === "youtube" || item?.type === "tiktok" ? (
                    <div
                      className="swiper-no-swiping"
                      style={{
                        height: isMobile ? "100%" : "609px",
                        width: "100%",
                      }}
                    >
                      <iframe
                        width="100%"
                        height="100%"
                        src={item.video_url}
                        title="Video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        style={{ pointerEvents: "auto" }}
                      ></iframe>
                    </div>
                  ) : (
                    <div
                      className="mobile-magnifier-container"
                      onClick={() => {
                        if (isMobile) {
                          openZoomModal(index);
                        }
                      }}
                      onTouchEnd={() => {
                        if (isMobile) {
                          openZoomModal(index);
                        }
                      }}
                    >
                      <Magnifier
                        key={srcValue}
                        className="setHeight"
                        height={isMobile ? "100%" : "30em"}
                        src={srcValue}
                        width="100%"
                        onClick={() => {
                          if (isMobile) {
                            openZoomModal(index);
                          }
                        }}
                        onTouchEnd={() => {
                          if (isMobile) {
                            openZoomModal(index);
                          }
                        }}
                      />
                    </div>
                  )}
                </SwiperSlide>
              );
            })}
        </Swiper>

        {/* ── Arrows specifically centered on the MAIN slider only ── */}
        {allUrl.length > 1 && (
          <>
            <button
              ref={prevRef}
              style={{ ...arrowStyle, left: "10px" }}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onClick={() => {
                if (mainSwiper && !mainSwiper.destroyed) {
                  mainSwiper.slidePrev();
                }
              }}
              className="swiper-button-prev-custom"
              aria-label="Previous image"
            >
              <FiChevronLeft />
            </button>
            <button
              ref={nextRef}
              style={{ ...arrowStyle, right: "10px" }}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onClick={() => {
                if (mainSwiper && !mainSwiper.destroyed) {
                  mainSwiper.slideNext();
                }
              }}
              className="swiper-button-next-custom"
              aria-label="Next image"
            >
              <FiChevronRight />
            </button>
          </>
        )}
      </div>

      {/* ── Modal overlay for mobile full-screen zoomed view ── */}
      {modalIndex !== null && zoomImages[modalIndex] && (
        <div
          className="image-modal"
          onClick={(e) => {
            // Clicking the backdrop closes the modal
            if (
              e.target === e.currentTarget ||
              e.target.classList.contains("image-modal-content-wrap")
            ) {
              closeZoomModal(e);
            }
          }}
          onTouchStart={handleModalTouchStart}
          onTouchEnd={handleModalTouchEnd}
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed Product Image View"
        >
          {/* Close button (×) */}
          <button
            type="button"
            className="image-modal-close"
            onClick={closeZoomModal}
            onTouchEnd={(e) => {
              e.stopPropagation();
              if (e.type === "touchend") e.preventDefault();
              closeZoomModal();
            }}
            aria-label="Close zoomed view"
            title="Close"
          >
            <FiX size={24} />
          </button>

          {/* Previous image button */}
          {zoomImages.length > 1 && (
            <button
              type="button"
              className="image-modal-nav prev"
              onClick={prevZoomImage}
              onTouchEnd={(e) => {
                e.stopPropagation();
                if (e.type === "touchend") e.preventDefault();
                prevZoomImage();
              }}
              aria-label="Previous image"
              title="Previous image"
            >
              <FiChevronLeft size={28} />
            </button>
          )}

          {/* Main Zoomed Image */}
          <div
            className="image-modal-content-wrap"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeZoomModal(e);
              }
            }}
          >
            <img
              src={zoomImages[modalIndex]?.src}
              alt={props.item?.name || "Product zoomed"}
              className="image-modal-img"
              key={zoomImages[modalIndex]?.src}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Next image button */}
          {zoomImages.length > 1 && (
            <button
              type="button"
              className="image-modal-nav next"
              onClick={nextZoomImage}
              onTouchEnd={(e) => {
                e.stopPropagation();
                if (e.type === "touchend") e.preventDefault();
                nextZoomImage();
              }}
              aria-label="Next image"
              title="Next image"
            >
              <FiChevronRight size={28} />
            </button>
          )}

          {/* Image index counter badge */}
          {zoomImages.length > 1 && (
            <div className="image-modal-counter">
              <span>{modalIndex + 1}</span> / <span>{zoomImages.length}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Thumbnails Slider (ONLY shown when there are 2 or more images) ── */}
      {allUrl && allUrl.length > 1 && (
        <div
          className="product-thumbs-slider-wrap"
          style={{
            width: "100%",
            marginTop: "12px",
          }}
        >
          <Swiper
            onSwiper={setActiveThumb}
            watchSlidesProgress={true}
            loop={false}
            spaceBetween={10}
            slidesPerView={allUrl.length <= 3 ? 3 : 4}
            centerInsufficientSlides={true}
            modules={[Thumbs]}
            className="product-images-slider-thumbs"
            style={{
              width: "100%",
              maxWidth: "100%",
              margin: "0 auto",
            }}
          >
            {allUrl.map((item, index) => {
              const isSelected = activeIndex === index;
              const thumbSrc =
                item && typeof item === "string"
                  ? item
                  : item?.original_image_url
                    ? item.original_image_url
                    : Placeholder;

              return (
                <SwiperSlide
                  key={index}
                  className={`product-thumb-slide ${
                    isSelected ? "thumb-active swiper-slide-thumb-active" : ""
                  }`}
                  onClick={() => handleThumbClick(index)}
                >
                  <div className="thumb-inner-box">
                    {item?.type === "video" ? (
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#000",
                        }}
                      >
                        <video
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          muted
                        >
                          <source src={item.video_url} type="video/mp4" />
                        </video>
                        <span
                          style={{
                            position: "absolute",
                            color: "#F5B96D",
                            fontSize: "20px",
                          }}
                        >
                          ▶
                        </span>
                      </div>
                    ) : item?.type === "youtube" ? (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          background: "#1f1f1f",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ff0000",
                        }}
                      >
                        <FaYoutube size={32} />
                      </div>
                    ) : item?.type === "tiktok" ? (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          background: "#000",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ffffff",
                        }}
                      >
                        <FaTiktok size={26} />
                      </div>
                    ) : (
                      <LazyLoadImage
                        onLoad={() => props.imageLoad && props.imageLoad()}
                        src={thumbSrc}
                        alt={`thumbnail-${index + 1}`}
                        className="thumb-img"
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      )}
    </div>
  );
};

ProductImagesSlider.propTypes = {
  images: PropTypes.array.isRequired,
};

export default ProductImagesSlider;